import { useEffect } from "react";
import { KEYBOARD_KEYS } from "../constants";
import { getFocusableElements } from "../utilities";
/**
 * Hook to prevent from tabbing out of a list of focusable elements
 * located under a given dom element (contentRef.current).
 *
 * @param {Ref} React.ref ref to a dom element
 * @param {Boolean} active A boolean indicating if this hook is active
 */

export default function useFocusTrap(contentRef, active) {
  useEffect(() => {
    if (active) {
      const listener = event => {
        if (event.key === KEYBOARD_KEYS.TAB) {
          const focusableEls = getFocusableElements(contentRef.current);
          /** If no focusable elements, nothing special to be done. */

          if (focusableEls.length < 1) {
            return;
          }
          /**
           * At least 2 focusable elements.
           * We need "trap" the focus order when:
           * - the last focusable element is focused and the user hits TAB
           *    => force focus on the first focusable element
           * - the first focusable element is focused and the user hits SHIFT+TAB
           *    => force focus on the last focusable element
           */


          const firstFocusableEl = focusableEls[0];
          const lastFocusableEl = focusableEls[focusableEls.length - 1];

          if (event.shiftKey && document.activeElement === firstFocusableEl) {
            event.preventDefault();
            lastFocusableEl.focus();
          } else if (!event.shiftKey && document.activeElement === lastFocusableEl) {
            event.preventDefault();
            firstFocusableEl.focus();
          }
        }
      };

      document.addEventListener("keydown", listener);
      return () => {
        document.removeEventListener("keydown", listener);
      };
    }
  }, [contentRef, active]);
}